import { Box, FormControlSizes, IconButton, IconName, useOnClickOutside } from '@talos/kyoko';
import type { TouchEvent } from 'react';
import { type PropsWithChildren, useCallback, useRef, useState } from 'react';
import { DragHandle, SheetModal } from './styles';

interface MobileModalSheetProps extends PropsWithChildren {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileModalSheet({ children, isOpen, onClose }: PropsWithChildren<MobileModalSheetProps>) {
  const ref = useRef<HTMLDivElement>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);
  const dragOffsetRef = useRef(0);

  // Calculate opacity based on drag progress (0.5 threshold)
  let threshold = 0;
  if (typeof window !== 'undefined') {
    const screenHeight = window.innerHeight;
    threshold = screenHeight * 0.5;
  }

  const handleTouchStart = useCallback(
    (e: TouchEvent<HTMLDivElement>) => {
      setIsDragging(true);
      startY.current = e.touches[0].clientY;
      dragOffsetRef.current = e.touches[0].clientY;
    },
    [dragOffsetRef]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent<HTMLDivElement>) => {
      if (!isDragging) {
        return;
      }

      dragOffsetRef.current = e.touches[0].clientY;
      const deltaY = dragOffsetRef.current - startY.current;

      // Only allow downward dragging
      if (deltaY > 0) {
        setDragOffset(deltaY);
      }
    },
    [isDragging, dragOffsetRef]
  );

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) {
      return;
    }

    const deltaY = dragOffsetRef.current - startY.current;
    if (deltaY > threshold) {
      onClose();
    }

    startY.current = 0;
    dragOffsetRef.current = 0;
    setDragOffset(0);
    setIsDragging(false);
  }, [isDragging, onClose, threshold, dragOffsetRef]);

  const handleClickOutside = useCallback(() => {
    if (!isOpen) {
      return;
    }
    onClose();
  }, [isOpen, onClose]);
  useOnClickOutside(ref, handleClickOutside);

  return (
    <SheetModal isOpen={isOpen} dragOffset={dragOffset} ref={ref}>
      <DragHandle onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} />
      {children}
      <Box position="absolute" top="16px" right="16px" zIndex={10}>
        <IconButton
          round
          dim
          icon={IconName.Close}
          size={FormControlSizes.Large}
          onClick={onClose}
          ghost
          data-testid="modal-sheet-close-button"
        />
      </Box>
    </SheetModal>
  );
}
